import React, { useState, useEffect, useCallback } from 'react';
import { supabase, insertRow, updateRow } from '../../db/engine';
import { useApp } from '../../context/AppContext';
import { Send, Printer, ChevronDown, ChevronUp, AlertTriangle, X, Check } from 'lucide-react';
import ClienteSelect from '../../components/ClienteSelect';
import { fmtFecha } from '../../utils/fecha';
import { marcaImpresion, getAbrev } from '../../utils/marca';
import { imprimirHTML } from '../../utils/imprimir';

import { fmtMoneda as fmt$ } from '../../utils/moneda';

export default function TabRemesas() {
  const { user } = useApp();
  const abrev = getAbrev();

  // Bolsas pendientes: cobros con donde_pago='RU' sin remesa, agrupados por aseguradora
  const [bolsas, setBolsas]           = useState([]); // [{ aseguradora_id, aseguradora_nombre, cobros[] }]
  const [remesas, setRemesas]         = useState([]); // historial de remesas enviadas
  const [expandidos, setExpandidos]   = useState({}); // { aseguradora_id: bool }
  const [expandidosHist, setExpandidosHist] = useState({}); // historial expandido

  // Modal confirmar envío
  const [modalEnviar, setModalEnviar]           = useState(null); // { aseguradora_id, aseguradora_nombre, cobros[] }
  const [incluyeComision, setIncluyeComision]   = useState(false);
  const [fechaRemesa, setFechaRemesa]           = useState(new Date().toISOString().slice(0, 10));
  const [notasRemesa, setNotasRemesa]           = useState('');
  const [enviando, setEnviando]                 = useState(false);

  // Filtros: aseguradora y cliente (bolsas + historial) y rango de fechas (historial)
  const [aseguradoras, setAseguradoras] = useState([]);
  const [filtroAseg, setFiltroAseg]     = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [desde, setDesde]               = useState('');
  const [hasta, setHasta]               = useState('');

  useEffect(() => {
    supabase.from('aseguradoras').select('id, nombre').eq('activa', true).order('nombre')
      .then(({ data }) => setAseguradoras(data || []));
  }, []);

  const load = useCallback(async () => {
    // 1. Cobros pendientes en RU sin remesa asignada
    const { data: cobrosPend } = await supabase.from('cobros_recibidos')
      .select('*, polizas(numero, aseguradora_id, cliente_id, aseguradoras(nombre), clientes(nombre), comision_ramo, impuesto, subramos(base_comision))')
      .eq('donde_pago', 'RU')
      .is('remesa_id', null)
      .or('anulado.is.null,anulado.eq.false')
      .order('fecha', { ascending: true });

    // Agrupar por aseguradora
    const porAseg = {};
    for (const c of (cobrosPend || [])) {
      const asegId   = c.polizas?.aseguradora_id;
      const asegNom  = c.polizas?.aseguradoras?.nombre || '—';
      if (!asegId) continue;
      if (!porAseg[asegId]) porAseg[asegId] = { aseguradora_id: asegId, aseguradora_nombre: asegNom, cobros: [] };
      // Calcular comisión estimada
      const monto    = parseFloat(c.monto) || 0;
      const comRamo  = parseFloat(c.polizas?.comision_ramo) || 0;
      const imp      = c.polizas?.impuesto == null ? 5 : (parseFloat(c.polizas?.impuesto) || 0);
      const base     = (c.polizas?.subramos?.base_comision === 'con_impuesto') ? monto : monto / (1 + imp / 100);
      const comision = Math.round(base * comRamo / 100 * 100) / 100;
      porAseg[asegId].cobros.push({
        ...c,
        poliza_num:         c.polizas?.numero || '—',
        cliente:            c.polizas?.clientes?.nombre || '—',
        cliente_id:         c.polizas?.cliente_id,
        comision_estimada:  comision,
        neto_estimado:      Math.round((monto - comision) * 100) / 100,
      });
    }
    setBolsas(Object.values(porAseg));

    // 2. Historial de remesas enviadas
    const { data: hist } = await supabase.from('remesas')
      .select(`*, aseguradoras(nombre),
        remesa_cobros(id, cobro_id, monto_bruto, monto_comision, monto_neto,
          cobros_recibidos(monto, fecha, polizas(numero, cliente_id, clientes(nombre))))`)
      .order('creado_en', { ascending: false });
    setRemesas(hist || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleBolsa = (id) => setExpandidos(p => ({ ...p, [id]: !p[id] }));
  const toggleHist  = (id) => setExpandidosHist(p => ({ ...p, [id]: !p[id] }));

  const abrirEnviar = (bolsa) => {
    setModalEnviar(bolsa);
    setIncluyeComision(false);
    setFechaRemesa(new Date().toISOString().slice(0, 10));
    setNotasRemesa('');
  };

  const calcTotales = (cobros, conComision) => {
    const bruto    = cobros.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
    const comision = conComision ? cobros.reduce((s, c) => s + c.comision_estimada, 0) : 0;
    return { bruto, comision, neto: bruto - comision };
  };

  const handleEnviar = async () => {
    if (!modalEnviar || !fechaRemesa) return;
    setEnviando(true);
    try {
      const { bruto, comision, neto } = calcTotales(modalEnviar.cobros, incluyeComision);

      // 1. Crear remesa
      const remesa = await insertRow('remesas', {
        aseguradora_id:   modalEnviar.aseguradora_id,
        fecha:            fechaRemesa,
        estado:           'enviada',
        incluye_comision: incluyeComision,
        total_bruto:      Math.round(bruto * 100) / 100,
        total_comision:   Math.round(comision * 100) / 100,
        total_neto:       Math.round(neto * 100) / 100,
        notas:            notasRemesa || null,
        creado_por:       user?.nombre || user?.email || 'Sistema',
      });

      // 2. Crear remesa_cobros, enlazar cobros y actualizar comisiones
      for (const cobro of modalEnviar.cobros) {
        const com  = incluyeComision ? cobro.comision_estimada : 0;
        const monto = parseFloat(cobro.monto) || 0;
        await insertRow('remesa_cobros', {
          remesa_id:      remesa.id,
          cobro_id:       cobro.id,
          monto_bruto:    monto,
          monto_comision: Math.round(com * 100) / 100,
          monto_neto:     Math.round((monto - com) * 100) / 100,
        });
        await updateRow('cobros_recibidos', cobro.id, { remesa_id: remesa.id });

        // Actualizar estado de comisión vinculada a este cobro
        const { data: coms } = await supabase.from('comisiones')
          .select('id').eq('cobro_id', cobro.id)
          .in('estado', ['pendiente', 'pendiente_aseg']);
        for (const c of (coms || [])) {
          await updateRow('comisiones', c.id, {
            estado: incluyeComision ? 'pendiente' : 'pendiente_aseg',
          });
        }
      }

      setModalEnviar(null);
      await load();
      alert(`✅ Remesa enviada a ${modalEnviar.aseguradora_nombre}.\n${modalEnviar.cobros.length} cobros · ${fmt$(neto)} ${incluyeComision ? '(comisión descontada)' : ''}`);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setEnviando(false); }
  };

  const imprimirRemesa = (remesa) => {
    const M = marcaImpresion();
    const aseg      = remesa.aseguradoras?.nombre || '—';
    const incluyeCom = remesa.incluye_comision;
    const filas = (remesa.remesa_cobros || []).map(rc => {
      const cr = rc.cobros_recibidos || {};
      return `<tr>
        <td>${cr.polizas?.numero || '—'}</td>
        <td>${cr.polizas?.clientes?.nombre || '—'}</td>
        <td>${fmtFecha(cr.fecha) || '—'}</td>
        <td style="text-align:right">${fmt$(rc.monto_bruto)}</td>
        ${incluyeCom ? `<td style="text-align:right;color:#b45309">${fmt$(rc.monto_comision)}</td>` : ''}
        <td style="text-align:right;font-weight:700">${fmt$(rc.monto_neto)}</td>
      </tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <title>Remesa — ${aseg} — ${fmtFecha(remesa.fecha)}</title>
      <style>
        body{font-family:Arial,sans-serif;font-size:11px;color:#111;margin:20px}
        h2{font-size:15px;margin-bottom:4px}
        .sub{font-size:11px;color:#555;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;font-size:10px}
        th{background:${M.colores.steel};color:#fff;padding:6px 4px;text-align:left}
        td{padding:5px 4px;border-bottom:1px solid #e0e0e0}
        tr:nth-child(even) td{background:#f7f9fb}
        .total-row td{font-weight:700;border-top:2px solid ${M.colores.steel};background:#e8f0f8}
        @media print{button{display:none}}
      </style></head><body>
      <h2>Remesa de Pagos — ${aseg}</h2>
      <div class="sub">Fecha: ${fmtFecha(remesa.fecha)} &nbsp;·&nbsp; ${(remesa.remesa_cobros||[]).length} cobros
        ${incluyeCom ? ` &nbsp;·&nbsp; <strong>Comisión ${M.abreviatura} descontada</strong>` : ''}
        &nbsp;·&nbsp; ${M.nombre}
      </div>
      <table><thead><tr>
        <th>Póliza</th><th>Cliente</th><th>Fecha Cobro</th><th>Monto Bruto</th>
        ${incluyeCom ? `<th>Comisión ${M.abreviatura}</th>` : ''}
        <th>Neto a Aseg.</th>
      </tr></thead><tbody>${filas}
        <tr class="total-row">
          <td colspan="3" style="text-align:right">TOTALES</td>
          <td style="text-align:right">${fmt$(remesa.total_bruto)}</td>
          ${incluyeCom ? `<td style="text-align:right">${fmt$(remesa.total_comision)}</td>` : ''}
          <td style="text-align:right">${fmt$(remesa.total_neto)}</td>
        </tr>
      </tbody></table>
      ${remesa.notas ? `<div style="margin-top:16px;font-size:11px;color:#555">Notas: ${remesa.notas}</div>` : ''}
    </body></html>`;
    // Iframe aislado (utils/imprimir.js): la ventana nueva con <script> ejecutaba lo que
    // trajeran las notas de la remesa. Tampoco depende de ventanas emergentes.
    imprimirHTML(html, (m) => alert(m.replace('No se pudo imprimir', 'No se pudo generar el PDF')));
  };

  // Cobros de una remesa que pertenecen al cliente filtrado (sin filtro = todos).
  const cobrosDeCliente = (r) => (r.remesa_cobros || []).filter(rc =>
    !filtroCliente || String(rc.cobros_recibidos?.polizas?.cliente_id) === String(filtroCliente));

  // Aplicar filtros. Aseguradora y cliente afectan ambas secciones; fechas solo el historial.
  // Con filtro de cliente, cada bolsa muestra SOLO los cobros pendientes de ese cliente.
  const bolsasFiltradas = bolsas
    .filter(b => !filtroAseg || String(b.aseguradora_id) === String(filtroAseg))
    .map(b => filtroCliente
      ? { ...b, cobros: b.cobros.filter(c => String(c.cliente_id) === String(filtroCliente)) }
      : b)
    .filter(b => b.cobros.length > 0);

  const remesasFiltradas = remesas.filter(r => {
    if (filtroAseg && String(r.aseguradora_id) !== String(filtroAseg)) return false;
    if (desde && (r.fecha || '') < desde) return false;
    if (hasta && (r.fecha || '') > hasta) return false;
    if (filtroCliente && cobrosDeCliente(r).length === 0) return false;
    return true;
  });
  const hayFiltro = filtroAseg || desde || hasta || filtroCliente;

  return (
    <div>
      {/* ── Filtros ── */}
      <div className="toolbar" style={{ marginBottom:8 }}>
        <select className="form-select" style={{ width:220 }} value={filtroAseg} onChange={e=>setFiltroAseg(e.target.value)}>
          <option value="">Todas las aseguradoras</option>
          {aseguradoras.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>
        <ClienteSelect value={filtroCliente} onChange={setFiltroCliente} placeholder="Filtrar por cliente..." width={230} />
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <label style={{ fontSize:12, color:'var(--silver)' }}>Desde</label>
          <input className="form-input" type="date" style={{ width:150 }} value={desde} onChange={e=>setDesde(e.target.value)} />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <label style={{ fontSize:12, color:'var(--silver)' }}>Hasta</label>
          <input className="form-input" type="date" style={{ width:150 }} value={hasta} onChange={e=>setHasta(e.target.value)} />
        </div>
        {hayFiltro && (
          <button className="btn btn-secondary btn-sm" onClick={()=>{ setFiltroAseg(''); setFiltroCliente(''); setDesde(''); setHasta(''); }}>Limpiar</button>
        )}
      </div>
      <div style={{ fontSize:11, color:'var(--silver)', marginBottom: filtroCliente ? 10 : 20 }}>El rango de fechas aplica al historial de remesas enviadas.</div>
      {filtroCliente && (
        <div style={{ padding:'10px 14px', background:'rgba(var(--sky-rgb),0.08)', border:'1px solid rgba(var(--sky-rgb),0.25)', borderRadius:8, fontSize:12, color:'var(--sky-light)', marginBottom:20 }}>
          Vista de consulta por cliente: abajo ves los cobros de este cliente <strong>pendientes de remesar</strong> y las
          <strong> remesas en las que ya se le pagaron a la aseguradora</strong>. Para enviar una remesa, limpia el filtro de
          cliente — la remesa incluye todos los cobros pendientes de la aseguradora, no solo los de un cliente.
        </div>
      )}

      {/* ── Bolsas pendientes ── */}
      <div style={{ fontSize:12, fontWeight:700, color:'var(--sky-light)', letterSpacing:1, marginBottom:14 }}>
        PENDIENTE DE REMESAR — PAGOS RECIBIDOS EN {abrev}
      </div>

      {bolsasFiltradas.length === 0 ? (
        <div style={{ padding:'20px 24px', background:'rgba(34, 197, 94, 0.06)', border:'1px solid rgba(34, 197, 94, 0.2)', borderRadius:10, fontSize:13, color:'var(--text-success)', marginBottom:28 }}>
          {bolsas.length === 0
            ? `✅ Sin cobros pendientes de remesar. Todos los pagos recibidos en ${abrev} ya fueron enviados a las aseguradoras.`
            : filtroCliente
              ? 'Este cliente no tiene cobros pendientes de remesar (todo lo recibido ya fue enviado a la aseguradora).'
              : 'Sin pendientes de remesar para la aseguradora seleccionada.'}
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:12, marginBottom:32 }}>
          {bolsasFiltradas.map(bolsa => {
            const totalBruto = bolsa.cobros.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
            const exp = expandidos[bolsa.aseguradora_id];
            return (
              <div key={bolsa.aseguradora_id} style={{ background:'rgba(245, 158, 11, 0.04)', borderRadius:12, border:'1px solid rgba(245, 158, 11, 0.25)', overflow:'hidden' }}>
                {/* Cabecera bolsa */}
                <div style={{ display:'flex', alignItems:'center', gap:16, padding:'14px 18px', cursor:'pointer' }}
                  onClick={() => toggleBolsa(bolsa.aseguradora_id)}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:3 }}>
                      <span style={{ fontWeight:700, fontSize:15 }}>{bolsa.aseguradora_nombre}</span>
                      <span style={{ fontSize:11, fontWeight:700, color:'var(--text-warning)', background:'rgba(245, 158, 11, 0.12)', padding:'2px 10px', borderRadius:5, border:'1px solid rgba(245, 158, 11, 0.3)' }}>
                        {bolsa.cobros.length} cobro(s) pendientes{filtroCliente ? ' de este cliente' : ''}
                      </span>
                    </div>
                    <div style={{ fontSize:12, color:'var(--silver)' }}>
                      {filtroCliente ? 'Total del cliente: ' : 'Total acumulado: '}
                      <strong style={{ color:'var(--text)', fontSize:14 }}>{fmt$(totalBruto)}</strong>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    {/* Con filtro de cliente NO se envía: la remesa abarca todos los cobros de la aseguradora */}
                    {user?.es_master && !filtroCliente && (
                      <button className="btn btn-sm btn-primary"
                        onClick={e => { e.stopPropagation(); abrirEnviar(bolsa); }}>
                        <Send size={12}/> Enviar remesa
                      </button>
                    )}
                    {exp ? <ChevronUp size={16} style={{ color:'var(--silver)' }}/> : <ChevronDown size={16} style={{ color:'var(--silver)' }}/>}
                  </div>
                </div>

                {/* Detalle cobros en bolsa */}
                {exp && (
                  <div style={{ borderTop:'1px solid rgba(245, 158, 11, 0.15)', padding:'12px 18px' }}>
                    <div className="table-wrapper">
                      <table>
                        <thead>
                          <tr><th>Póliza</th><th>Cliente</th><th>Fecha</th><th>Monto</th><th>Recibo</th></tr>
                        </thead>
                        <tbody>
                          {bolsa.cobros.map((c, i) => (
                            <tr key={i}>
                              <td style={{ fontWeight:700, color:'var(--sky)', fontSize:12 }}>{c.poliza_num}</td>
                              <td style={{ fontSize:12 }}>{c.cliente}</td>
                              <td style={{ fontSize:12, color:'var(--silver)' }}>{fmtFecha(c.fecha)}</td>
                              <td style={{ fontWeight:600 }}>{fmt$(c.monto)}</td>
                              <td style={{ fontSize:11, color:'var(--silver)', fontFamily:'monospace' }}>{c.num_recibo_ru || c.num_recibo_aseg || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Historial de remesas enviadas ── */}
      <div style={{ fontSize:12, fontWeight:700, color:'var(--sky-light)', letterSpacing:1, marginBottom:14 }}>
        HISTORIAL DE REMESAS ENVIADAS
      </div>

      {remesasFiltradas.length === 0 ? (
        <div style={{ fontSize:13, color:'var(--silver)', padding:'16px 0' }}>
          {remesas.length === 0
            ? 'Sin remesas enviadas aún.'
            : filtroCliente
              ? 'Este cliente no aparece en ninguna remesa enviada con los filtros actuales.'
              : 'Sin remesas para el filtro seleccionado.'}
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {remesasFiltradas.map(r => {
            const exp = expandidosHist[r.id];
            const delCliente = cobrosDeCliente(r);
            const brutoCliente = delCliente.reduce((s, rc) => s + (parseFloat(rc.monto_bruto) || 0), 0);
            return (
              <div key={r.id} style={{ background:'rgba(var(--navy-rgb),0.4)', borderRadius:12, border:'1px solid rgba(var(--sky-rgb),0.15)', overflow:'hidden' }}>
                <div style={{ display:'flex', alignItems:'center', gap:16, padding:'12px 18px', cursor:'pointer' }}
                  onClick={() => toggleHist(r.id)}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:3 }}>
                      <span style={{ fontWeight:700 }}>{r.aseguradoras?.nombre || '—'}</span>
                      <span style={{ fontSize:11, color:'var(--text-success)' }}>Enviada</span>
                      {r.incluye_comision && <span style={{ fontSize:11, color:'var(--text-warning)' }}>Com. descontada</span>}
                    </div>
                    <div style={{ fontSize:12, color:'var(--silver)' }}>
                      {fmtFecha(r.fecha)} &nbsp;·&nbsp; {(r.remesa_cobros||[]).length} cobros
                      &nbsp;·&nbsp; Bruto: <strong>{fmt$(r.total_bruto)}</strong>
                      {r.incluye_comision && <> &nbsp;·&nbsp; Com: <strong style={{ color:'var(--text-warning)' }}>{fmt$(r.total_comision)}</strong></>}
                      &nbsp;·&nbsp; Neto: <strong style={{ color:'var(--text-success)' }}>{fmt$(r.total_neto)}</strong>
                    </div>
                    {filtroCliente && (
                      <div style={{ fontSize:12, color:'var(--sky-light)', marginTop:3 }}>
                        De este cliente: <strong>{delCliente.length} cobro(s)</strong> · <strong>{fmt$(brutoCliente)}</strong>
                      </div>
                    )}
                  </div>
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <button className="btn btn-sm btn-secondary" onClick={e => { e.stopPropagation(); imprimirRemesa(r); }}>
                      <Printer size={12}/> Imprimir
                    </button>
                    {exp ? <ChevronUp size={16} style={{ color:'var(--silver)' }}/> : <ChevronDown size={16} style={{ color:'var(--silver)' }}/>}
                  </div>
                </div>

                {exp && (
                  <div style={{ borderTop:'1px solid rgba(var(--sky-rgb),0.12)', padding:'12px 18px' }}>
                    {r.notas && <div style={{ fontSize:12, color:'var(--silver)', marginBottom:10 }}>📝 {r.notas}</div>}
                    {filtroCliente && (
                      <div style={{ fontSize:11, fontWeight:700, color:'var(--silver)', letterSpacing:1, marginBottom:6 }}>
                        COBROS DE ESTE CLIENTE EN LA REMESA
                      </div>
                    )}
                    <div className="table-wrapper">
                      <table>
                        <thead>
                          <tr>
                            <th>Póliza</th><th>Cliente</th><th>Fecha Cobro</th>
                            <th>Monto Bruto</th>
                            {r.incluye_comision && <th>Comisión</th>}
                            <th>Neto Aseg.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {delCliente.map((rc, i) => (
                            <tr key={i}>
                              <td style={{ fontWeight:700, color:'var(--sky)', fontSize:12 }}>{rc.cobros_recibidos?.polizas?.numero || '—'}</td>
                              <td style={{ fontSize:12 }}>{rc.cobros_recibidos?.polizas?.clientes?.nombre || '—'}</td>
                              <td style={{ fontSize:12, color:'var(--silver)' }}>{fmtFecha(rc.cobros_recibidos?.fecha) || '—'}</td>
                              <td style={{ fontWeight:600 }}>{fmt$(rc.monto_bruto)}</td>
                              {r.incluye_comision && <td style={{ color:'var(--text-warning)', fontWeight:600 }}>{fmt$(rc.monto_comision)}</td>}
                              <td style={{ fontWeight:700, color:'var(--text-success)' }}>{fmt$(rc.monto_neto)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {r.creado_por && <div style={{ fontSize:11, color:'var(--silver)', marginTop:8 }}>Enviada por {r.creado_por}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modal confirmar envío de remesa ── */}
      {modalEnviar && (() => {
        const { bruto, comision, neto } = calcTotales(modalEnviar.cobros, incluyeComision);
        return (
          <div className="modal-overlay" onClick={() => setModalEnviar(null)}>
            <div className="modal modal-lg" style={{ maxWidth:640 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <div className="modal-title">Enviar Remesa — {modalEnviar.aseguradora_nombre}</div>
                  <div style={{ fontSize:12, color:'var(--silver)', marginTop:2 }}>{modalEnviar.cobros.length} cobros acumulados</div>
                </div>
                <button className="btn btn-icon btn-secondary btn-sm" onClick={() => setModalEnviar(null)}><X size={16}/></button>
              </div>

              <div className="modal-body" style={{ maxHeight:'70vh', overflowY:'auto' }}>
                {/* Fecha y toggle comisión */}
                <div className="form-grid form-grid-2" style={{ marginBottom:16 }}>
                  <div className="form-group">
                    <label className="form-label">Fecha de envío *</label>
                    <input className="form-input" type="date" value={fechaRemesa} onChange={e => setFechaRemesa(e.target.value)}/>
                  </div>
                </div>

                <div style={{ marginBottom:16, padding:'12px 16px', background:'rgba(var(--steel-rgb),0.35)', borderRadius:10 }}>
                  <label style={{ display:'flex', alignItems:'flex-start', gap:12, cursor:'pointer' }}>
                    <input type="checkbox" checked={incluyeComision} onChange={e => setIncluyeComision(e.target.checked)}
                      style={{ width:16, height:16, cursor:'pointer', marginTop:2 }}/>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600 }}>Descontar comisión de {abrev}</div>
                      <div style={{ fontSize:11, color:'var(--silver)', marginTop:2 }}>
                        {abrev} retiene su comisión y envía solo el neto. Las comisiones pasan a "Pendiente Agente" de inmediato.<br/>
                        Si no se marca, se manda todo y la aseguradora paga la comisión de vuelta más adelante.
                      </div>
                    </div>
                  </label>
                </div>

                {/* Resumen totales */}
                <div style={{ display:'flex', gap:24, padding:'14px 18px', background:'rgba(var(--sky-rgb),0.08)', border:'1px solid rgba(var(--sky-rgb),0.2)', borderRadius:10, marginBottom:16 }}>
                  <div>
                    <div style={{ fontSize:11, color:'var(--silver)', marginBottom:2 }}>TOTAL BRUTO</div>
                    <div style={{ fontSize:22, fontWeight:800 }}>{fmt$(bruto)}</div>
                  </div>
                  {incluyeComision && (
                    <div>
                      <div style={{ fontSize:11, color:'var(--silver)', marginBottom:2 }}>COMISIÓN {abrev}</div>
                      <div style={{ fontSize:22, fontWeight:800, color:'var(--text-warning)' }}>{fmt$(comision)}</div>
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize:11, color:'var(--silver)', marginBottom:2 }}>NETO A ASEGURADORA</div>
                    <div style={{ fontSize:22, fontWeight:800, color:'var(--text-success)' }}>{fmt$(neto)}</div>
                  </div>
                </div>

                {/* Lista de cobros incluidos */}
                <div style={{ fontSize:11, fontWeight:700, color:'var(--silver)', letterSpacing:1, marginBottom:8 }}>COBROS INCLUIDOS</div>
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>Póliza</th><th>Cliente</th><th>Fecha</th><th>Monto</th>
                        {incluyeComision && <th>Comisión</th>}
                        <th>Neto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalEnviar.cobros.map((c, i) => (
                        <tr key={i}>
                          <td style={{ fontWeight:700, color:'var(--sky)', fontSize:12 }}>{c.poliza_num}</td>
                          <td style={{ fontSize:12 }}>{c.cliente}</td>
                          <td style={{ fontSize:12, color:'var(--silver)' }}>{fmtFecha(c.fecha)}</td>
                          <td style={{ fontWeight:600 }}>{fmt$(c.monto)}</td>
                          {incluyeComision && <td style={{ color:'var(--text-warning)' }}>{fmt$(c.comision_estimada)}</td>}
                          <td style={{ color: incluyeComision ? '#22c55e' : undefined }}>{fmt$(incluyeComision ? c.neto_estimado : c.monto)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="form-group" style={{ marginTop:16 }}>
                  <label className="form-label">Notas para la aseguradora (opcional)</label>
                  <textarea className="form-input" rows={2} value={notasRemesa} onChange={e => setNotasRemesa(e.target.value)}
                    style={{ resize:'vertical', fontFamily:'inherit' }} placeholder="Referencia de pago, observaciones, etc."/>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setModalEnviar(null)}>Cancelar</button>
                <button className="btn btn-success" onClick={handleEnviar} disabled={enviando || !fechaRemesa}>
                  <Send size={14}/> {enviando ? 'Procesando...' : `Confirmar envío — ${fmt$(neto)} a ${modalEnviar.aseguradora_nombre}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
